import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const SolarDeviceDashboard = ({ device, theme, apiBase }) => {
  const [solarData, setSolarData] = useState({
    currentPower: 0,
    todayEnergy: 0,
    totalEnergy: 0,
    todayEarnings: 0,
    dcVoltage: 0,
    temperature: 0,
    status: 'offline'
  });
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('unknown');

  // Strompreis pro kWh (kann später konfigurierbar gemacht werden)
  const electricityPrice = 0.32; // €/kWh

  useEffect(() => {
    fetchSolarData();
    const interval = setInterval(fetchSolarData, 30000); // Update alle 30 Sekunden
    return () => clearInterval(interval);
  }, [device]);

  const fetchSolarData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch real data from VPS Solar API
      const baseUrl = apiBase || "https://clara360.de/api/solar";
      const currentDataResponse = await fetch(`${baseUrl}/current`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000
      });

      if (!currentDataResponse.ok) {
        throw new Error(`API Error: ${currentDataResponse.status}`);
      }

      const currentDataResult = await currentDataResponse.json();
      
      if (currentDataResult.success && currentDataResult.data) {
        const apiData = currentDataResult.data;
        
        // Map API data to component state
        setSolarData({
          currentPower: Math.round(apiData.acPower || 0),
          todayEnergy: parseFloat(apiData.todayEnergy || 0),
          totalEnergy: parseFloat(apiData.totalEnergy || 0),
          todayEarnings: parseFloat(apiData.todayEarnings || 0),
          dcVoltage: Math.round(apiData.dcVoltage || 0),
          temperature: Math.round(apiData.temperature || 0),
          status: apiData.status === 'generating' ? 'online' : 
                  apiData.status === 'standby' ? 'standby' : 'offline'
        });
        
        setDataSource(currentDataResult.dataSource || 'api');
        
        // Fetch historical data
        try {
          const historyResponse = await fetch(`${baseUrl}/history?period=24h&interval=1h`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            timeout: 10000
          });

          if (historyResponse.ok) {
            const historyResult = await historyResponse.json();
            
            if (historyResult.success && historyResult.data && historyResult.data.length > 0) {
              // Transform API historical data for chart
              const chartData = historyResult.data.map(point => ({
                time: new Date(point.timestamp).toLocaleTimeString('de-DE', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                }),
                power: Math.round(point.acPower || 0),
                energy: parseFloat(point.todayEnergy || 0)
              }));
              setHistoricalData(chartData);
            } else {
              // Generate fallback hourly data based on current power
              generateFallbackHistoricalData(apiData.acPower || 0);
            }
          } else {
            // Generate fallback data if history API fails
            generateFallbackHistoricalData(apiData.acPower || 0);
          }
        } catch (historyError) {
          console.warn('Failed to fetch historical data, using fallback:', historyError);
          generateFallbackHistoricalData(apiData.acPower || 0);
        }
        
      } else {
        throw new Error('Invalid API response format');
      }
      
    } catch (error) {
      console.error('Fehler beim Laden der Solar-Daten:', error);
      setError(error.message);
      
      // Fallback to simulated data if API fails
      generateFallbackData();
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackHistoricalData = (currentPower) => {
    const now = new Date();
    const hour = now.getHours();
    const hourlyData = [];
    
    // Generate realistic hourly progression leading to current power
    for (let h = Math.max(6, hour - 12); h <= hour; h++) {
      const progress = (h - 6) / 14; // 6-20 Uhr = 14 Stunden
      const intensity = Math.sin(progress * Math.PI);
      const power = h === hour ? currentPower : Math.round(intensity * currentPower * 1.2);
      
      hourlyData.push({
        time: `${h.toString().padStart(2, '0')}:00`,
        power: Math.max(0, power),
        energy: Math.max(0, power * 0.8 / 1000) // kWh für diese Stunde
      });
    }
    setHistoricalData(hourlyData);
  };

  const generateFallbackData = () => {
    // Fallback simulated data when API is unavailable
    const now = new Date();
    const hour = now.getHours();
    
    let currentPower = 0;
    if (hour >= 6 && hour <= 20) {
      const dayProgress = (hour - 6) / 14;
      const solarIntensity = Math.sin(dayProgress * Math.PI);
      currentPower = Math.round(solarIntensity * 800 + Math.random() * 100);
    }
    
    const todayEnergy = Math.round((currentPower * (hour - 6) * 0.8) / 1000 * 100) / 100;
    const todayEarnings = Math.round(todayEnergy * electricityPrice * 100) / 100;
    
    setSolarData({
      currentPower: currentPower,
      todayEnergy: Math.max(0, todayEnergy),
      totalEnergy: 2855 + todayEnergy,
      todayEarnings: Math.max(0, todayEarnings),
      dcVoltage: currentPower > 0 ? 380 + Math.random() * 40 : 0,
      temperature: 20 + Math.random() * 15,
      status: currentPower > 0 ? 'online' : 'standby'
    });
    
    setDataSource('fallback');
    generateFallbackHistoricalData(currentPower);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'text-green-500';
      case 'standby': return 'text-yellow-500';
      case 'offline': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'Erzeugung aktiv';
      case 'standby': return 'Bereitschaft';
      case 'offline': return 'Offline';
      default: return 'Unbekannt';
    }
  };

  const getDataSourceBadge = () => {
    switch (dataSource) {
      case 'real':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          🔗 Live-Daten
        </span>;
      case 'simulated':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          🎭 Simuliert
        </span>;
      case 'fallback':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          ⚠️ Fallback
        </span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          ❓ Unbekannt
        </span>;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Solar-Anlage {device?.name || 'Hauptanlage'}
          </h2>
          <div className="flex items-center space-x-2 mt-1">
            <span className={`font-medium ${getStatusColor(solarData.status)}`}>
              {getStatusText(solarData.status)}
            </span>
            {getDataSourceBadge()}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">
            {solarData.currentPower.toLocaleString()} W
          </div>
          <div className="text-sm text-gray-500">Aktuelle Leistung</div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="text-red-400">⚠️</div>
            <div className="ml-2">
              <div className="text-sm text-red-800">
                API-Verbindungsfehler: {error}
              </div>
              <div className="text-xs text-red-600 mt-1">
                Fallback-Daten werden angezeigt
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kennzahlen Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">
            {solarData.todayEnergy.toFixed(1)}
          </div>
          <div className="text-sm text-blue-800">kWh heute</div>
        </div>
        
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">
            {solarData.totalEnergy.toLocaleString()}
          </div>
          <div className="text-sm text-green-800">kWh gesamt</div>
        </div>
        
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-600">
            {solarData.dcVoltage}
          </div>
          <div className="text-sm text-yellow-800">V DC</div>
        </div>
        
        <div className="bg-red-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-red-600">
            {solarData.temperature}°
          </div>
          <div className="text-sm text-red-800">Temperatur</div>
        </div>
      </div>

      {/* Ertrag heute */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              Ertrag heute: {solarData.todayEarnings.toFixed(2)} €
            </div>
            <div className="text-sm text-gray-600">
              bei {electricityPrice} €/kWh
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">Einsparung CO₂</div>
            <div className="font-semibold text-green-600">
              {(solarData.todayEnergy * 0.4).toFixed(1)} kg
            </div>
          </div>
        </div>
      </div>

      {/* Leistungsverlauf Chart */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Leistungsverlauf heute
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={historicalData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time" 
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                label={{ value: 'Leistung (W)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                formatter={(value, name) => [
                  `${value} W`, 
                  'Leistung'
                ]}
                labelFormatter={(label) => `Zeit: ${label}`}
              />
              <Line 
                type="monotone" 
                dataKey="power" 
                stroke="#3B82F6" 
                strokeWidth={2}
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5, stroke: '#3B82F6', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Footer Info */}
      <div className="text-xs text-gray-500 border-t pt-3">
        <div className="flex justify-between">
          <span>Letzte Aktualisierung: {new Date().toLocaleTimeString('de-DE')}</span>
          <span>Datenquelle: {dataSource === 'real' ? 'Live API' : 
                              dataSource === 'simulated' ? 'Simuliert' : 
                              dataSource === 'fallback' ? 'Fallback' : 'Unbekannt'}</span>
        </div>
      </div>
    </div>
  );
};

export default SolarDeviceDashboard;

